import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, ArrowLeftRight, Send, Download, Clock } from 'lucide-react';
import { useState, useCallback, memo } from 'react';

const navItems = [
  { path: '/dashboard', icon: Home, label: 'Главная' },
  { path: '/exchange', icon: ArrowLeftRight, label: 'Обмен' },
  { path: '/send', icon: Send, label: 'Отправить' },
  { path: '/receive', icon: Download, label: 'Получить' },
  { path: '/transactions', icon: Clock, label: 'История' },
];

function RippleEffect({ x, y }) {
  return (
    <motion.span
      initial={{ scale: 0, opacity: 0.5 }}
      animate={{ scale: 3, opacity: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="absolute rounded-full pointer-events-none"
      style={{
        left: x - 20, top: y - 20, width: 40, height: 40,
        background: 'radial-gradient(circle, rgba(0,240,255,0.35), transparent 70%)',
      }}
    />
  );
}

export default memo(function MobileBottomNav() {
  const location = useLocation();
  const [ripples, setRipples] = useState([]);

  const handleTap = useCallback((e, path) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setRipples(prev => [...prev, { id, x, y, path }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 700);
  }, []);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      style={{
        background: 'rgba(6,10,18,0.92)',
        backdropFilter: 'blur(24px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
        borderTop: '1px solid rgba(0,240,255,0.08)',
        boxShadow: '0 -4px 30px rgba(0,0,0,0.4), 0 -1px 0 rgba(0,240,255,0.04)',
      }}
      data-testid="mobile-bottom-nav"
    >
      {/* Neon top accent line */}
      <div className="absolute top-0 left-[15%] right-[15%] h-[1px]"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,240,255,0.25), rgba(255,0,60,0.12), transparent)' }}
      />

      <div className="flex items-center justify-around h-16 px-2 relative">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={(e) => handleTap(e, item.path)}
              className="relative flex flex-col items-center justify-center flex-1 h-full overflow-hidden"
              data-testid={`mob-nav-${item.label.toLowerCase()}`}
            >
              {/* Ripples */}
              <AnimatePresence>
                {ripples.filter(r => r.path === item.path).map(r => (
                  <RippleEffect key={r.id} x={r.x} y={r.y} />
                ))}
              </AnimatePresence>

              {/* Active glow background */}
              <AnimatePresence>
                {isActive && (
                  <motion.div
                    layoutId="activeNavBg"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    className="absolute inset-x-2 top-1.5 bottom-1.5 rounded-2xl"
                    style={{
                      background: 'linear-gradient(180deg, rgba(0,240,255,0.08), rgba(0,240,255,0.02))',
                      border: '1px solid rgba(0,240,255,0.12)',
                      boxShadow: '0 0 20px rgba(0,240,255,0.06), inset 0 1px 0 rgba(0,240,255,0.05)',
                    }}
                  />
                )}
              </AnimatePresence>

              {/* Icon */}
              <motion.div
                className="relative z-10"
                whileTap={{ scale: 0.85 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
              >
                <Icon
                  size={20}
                  strokeWidth={isActive ? 2.2 : 1.6}
                  className="transition-colors duration-200"
                  style={{
                    color: isActive ? '#00F0FF' : 'rgba(255,255,255,0.35)',
                    filter: isActive ? 'drop-shadow(0 0 6px rgba(0,240,255,0.4))' : 'none',
                  }}
                />
              </motion.div>

              {/* Active neon dot */}
              <AnimatePresence>
                {isActive && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25, delay: 0.05 }}
                    className="absolute top-1 w-1 h-1 rounded-full"
                    style={{
                      background: '#FF003C',
                      boxShadow: '0 0 6px rgba(255,0,60,0.6), 0 0 12px rgba(255,0,60,0.3)',
                    }}
                  />
                )}
              </AnimatePresence>

              {/* Label */}
              <span
                className="relative z-10 text-[10px] mt-0.5 font-medium transition-colors duration-200"
                style={{
                  color: isActive ? '#00F0FF' : 'rgba(255,255,255,0.3)',
                  textShadow: isActive ? '0 0 8px rgba(0,240,255,0.3)' : 'none',
                }}
              >
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>

      {/* Safe area padding for notched phones */}
      <div className="h-[env(safe-area-inset-bottom)]" style={{ background: 'rgba(6,10,18,0.92)' }} />
    </nav>
  );
});
